import { useEffect, useRef, useState } from "react";
import { QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { lerCartelaDaEtiqueta, type TipoVeiculoLaudo } from "@shared/etiqueta-qrcode";

const MSG_CAMERA = "Não foi possível abrir a câmera. Digite a cartela manualmente.";

interface LerQrCartelaButtonProps {
  tipoVeiculo: TipoVeiculoLaudo | null | undefined;
  onCartela: (cartela: string) => void;
  disabled?: boolean;
}

/** Lê o QR Code da etiqueta e devolve só a cartela (10 dígitos). Qualquer erro deixa o campo como estava. */
export function LerQrCartelaButton({ tipoVeiculo, onCartela, disabled }: LerQrCartelaButtonProps) {
  // ponteiro principal de toque = celular/tablet (não é largura de tela: tablet entra, janela estreita de computador não)
  const [temToque] = useState(() => window.matchMedia("(pointer: coarse)").matches);
  const [aberto, setAberto] = useState(false);
  const [abrindo, setAbrindo] = useState(false); // biblioteca/câmera ainda não ligaram
  const [naoRastreavel, setNaoRastreavel] = useState<string | null>(null);
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const { toast } = useToast();
  // o efeito da câmera só roda ao abrir/montar o <video>; ele lê as props atuais por aqui
  const atual = useRef({ tipoVeiculo, onCartela, toast });
  atual.current = { tipoVeiculo, onCartela, toast };

  useEffect(() => {
    if (!aberto || !video) return;
    let cancelado = false;
    let controles: { stop: () => void } | undefined;
    let lido = false;

    const falhaDeCamera = () => {
      setAberto(false);
      atual.current.toast({ variant: "destructive", title: "Câmera indisponível", description: MSG_CAMERA });
    };
    const aplicar = (texto: string) => {
      const { tipoVeiculo: tipo, onCartela: aoLer, toast: avisar } = atual.current;
      const r = tipo ? lerCartelaDaEtiqueta(texto, tipo) : null;
      if (!r) return;
      if (r.ok) return aoLer(r.cartela);
      if (r.erro === "nao_rastreavel") return setNaoRastreavel(r.mensagem);
      avisar({ variant: "destructive", title: "Etiqueta não aceita", description: r.mensagem });
    };

    if (!navigator.mediaDevices?.getUserMedia) {
      falhaDeCamera();
      return;
    }
    // a biblioteca só é baixada quando alguém abre a câmera (fora do bundle inicial); falha no download = câmera indisponível
    setAbrindo(true);
    import("@zxing/browser")
      .then(({ BrowserQRCodeReader }) =>
        cancelado
          ? undefined
          : new BrowserQRCodeReader().decodeFromConstraints({ video: { facingMode: "environment" } }, video, (resultado, _erro, ctl) => {
              // o leitor chama o callback sem parar (inclusive sem QR no quadro): só a primeira leitura vale
              // cancelado: o cleanup já rodou, mas `controles` ainda não existia para parar a câmera
              if (cancelado || !resultado || lido) return;
              lido = true;
              ctl.stop();
              setAberto(false);
              aplicar(resultado.getText());
            }),
      )
      .then((c) => {
        if (!c) return;
        if (cancelado) c.stop();
        else {
          controles = c;
          setAbrindo(false);
        }
      })
      .catch(() => {
        if (!cancelado) falhaDeCamera();
      });

    return () => {
      cancelado = true;
      controles?.stop();
      setAbrindo(false);
    };
  }, [aberto, video]);

  if (!temToque) return null; // depois de todos os hooks: a ordem deles não pode mudar

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="gap-2 shrink-0"
        disabled={disabled || !tipoVeiculo}
        title={tipoVeiculo ? undefined : "Selecione o veículo para ler o QR Code"}
        onClick={() => setAberto(true)}
      >
        <QrCode className="w-4 h-4" />
        Ler QR Code
      </Button>

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Ler QR Code da etiqueta</DialogTitle>
            <DialogDescription>Aponte a câmera para o QR Code da etiqueta.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <video ref={setVideo} muted playsInline className="w-full aspect-square rounded-md bg-black object-cover" />
            {abrindo && (
              <p role="status" className="absolute inset-0 flex items-center justify-center text-sm text-white">
                Abrindo câmera…
              </p>
            )}
          </div>
          <Button type="button" variant="outline" onClick={() => setAberto(false)}>
            Cancelar
          </Button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={naoRastreavel !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Etiqueta não aceita</AlertDialogTitle>
            <AlertDialogDescription>{naoRastreavel}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setNaoRastreavel(null)}>Entendi</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
